import React, { useCallback, useEffect, useState } from "react";
// -- Fabric UI

import { Badge, Button, Persona } from "@fluentui/react-components";
import {
  Alert24Regular,
  AlertRegular,
  GridDots28Filled,
  GridDotsFilled,
  PersonFilled,
} from "@fluentui/react-icons";
import AppLogo from "assets/appLogo.svg";
import { ChatPanel } from "components/ChatPanel/ChatPanel";
import { Flex, StyledTooltip } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { devices, sizes } from "library/constants";
import { INotification, SelectItem } from "library/types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GrecoPanel } from "./GrecoPanel/GrecoPanel";
import { IGrecoPanelStrings } from "./GrecoPanel/IGrecoPanelStrings";
import { PanelType } from "./GrecoPanel/PanelType";
import { HeaderTick } from "./HeaderTick";
import { IGrecoHeaderStrings } from "./IGrecoHeaderStrings";
import { NavigationPanel } from "./NavigationPanel/NavigationPanel";
import { AppName } from "components/AppName/AppName";
import { HeaderDocumentLinks } from "components/HeaderDocumentLinks/HeaderDocumentLinks";
import { SettingsMenu } from "./GrecoPanel/SettingsMenu/SettingsMenu";
import { UserSettingsMenu } from "./GrecoPanel/UserSettingsMenu/UserSettingsMenu";

interface IProps {
  headerConfig?: any;
  user?: any;
  panelStatus?: (status: boolean) => void;
  logo?: any;
  logout?: () => void;
  darkMode?: any;
  headerStrings?: IGrecoHeaderStrings;
  notifications?: INotification[];
  navigation?: JSX.Element;
  clearNotifications?: () => void;
  languageOptions?: SelectItem[];
  defaultLanguage?: string;
  onChangeLanguage: (
    event: React.FormEvent<HTMLDivElement>,
    option?: SelectItem,
    index?: number
  ) => void;
  isMobile?: boolean;
}

type statePanelType = PanelType | null;

const GrecoHeader = (props: IProps) => {
  const { t } = useTranslation();
  const theme = useAppTheme();
  const [panelType, setPanelType] = useState<statePanelType>(null);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [panelStrings, setPanelStrings] = useState<
    IGrecoPanelStrings | undefined
  >();
  const [headerConfig, setHeaderConfig] = useState({
    application_name: "GrECo Header",
    notifications_visible: true,
    help_visible: true,
    account: true,
    search_autofocus: false,
    logo_visible: true,
    title_visible: true,
    settings_visible: true,
    defaultLanguage: "en-GB",
    navigationPanelOpen: false,
    isMobile: true,
  });

  const onNavigationStateChange = useCallback((value) => {
    setHeaderConfig((prev) => ({
      ...prev,
      navigationPanelOpen: value,
    }));
  }, []);

  useEffect(() => {
    if (!props.headerConfig) return;
    setHeaderConfig((prev) => ({ ...prev, ...props.headerConfig }));
  }, [props.headerConfig]);

  useEffect(() => {
    if (!props.headerStrings || !panelType) return;

    if (panelType !== null) {
      const panelStr = updatePanelStrings(
        panelType,
        props.headerStrings
      ) as IGrecoPanelStrings;
      setPanelStrings(panelStr);
    }
  }, [panelType, props.headerStrings]);

  const updatePanelStrings = (
    panelType: PanelType,
    headerStrings
  ): IGrecoPanelStrings | undefined => {
    switch (panelType) {
      case PanelType.Notifications:
        return headerStrings.notificationsPanelStrings;
      default:
        return undefined;
    }
  };

  const togglePanel = (type) =>
    type === panelType ? closePanel() : openPanel(type);

  const openPanel = (type) => {
    if (props.panelStatus) props.panelStatus(true);
    setPanelIsOpen(true);
    setPanelType(type);
  };

  const closePanel = () => {
    if (props.panelStatus) props.panelStatus(false);
    setPanelIsOpen(false);
    setPanelType(null);
  };

  return (
    <Container>
      <GrecoPanel
        onDismiss={closePanel}
        panelIsOpen={panelIsOpen}
        panelType={panelType}
        userData={props.user}
        logout={props.logout as () => void}
        headerConfig={headerConfig}
        darkMode={props.darkMode}
        panelStrings={panelStrings}
        notifications={props.notifications}
        clearNotifications={props.clearNotifications}
        isMobile={headerConfig.isMobile}
        togglePanel={togglePanel as () => void}
        languageOptions={props.languageOptions}
        defaultSelectedLanguage={
          props.defaultLanguage || headerConfig.defaultLanguage
        }
        onChangeLanguage={props.onChangeLanguage}
      />
      {props.navigation && (
        <>
          <StyledTooltip
            content={t("hbh.navigation.label")}
            relationship="description"
          >
            <NavigationButton
              appearance="outline"
              onClick={() => {
                onNavigationStateChange(true);
              }}
              icon={
                <GridDotsFilled
                  style={{
                    width: "30px",
                    height: "30px",
                  }}
                />
              }
            />
          </StyledTooltip>
          <HeaderTick />
        </>
      )}
      <NavigationPanel
        navigation={props.navigation}
        onNavigationStateChange={onNavigationStateChange}
        navigationPanelOpen={headerConfig.navigationPanelOpen}
      />
      <ChatPanel />

      <ContentWrap>
        <Flex>
          {headerConfig.logo_visible ? (
            <img
              className="logo"
              src={props.logo}
              alt={headerConfig.application_name}
              style={{
                height: "38px",
              }}
            />
          ) : null}
          <img
            src={AppLogo}
            style={{
              marginLeft: "10px",
              width: "41px",
              height: "38px",
              background: props.darkMode ? "transparent" : theme.palette.white,
            }}
            alt="app_logo"
          />
          {headerConfig.logo_visible ? <AppName /> : null}
        </Flex>
        <ActionIconsWrap>
          <HeaderDocumentLinks />
          <NotificationsButtonWrap>
            <Button
              icon={
                <AlertRegular
                  fontSize={24}
                  style={{
                    paddingTop: 4,
                  }}
                />
              }
              onClick={() => togglePanel(PanelType.Notifications)}
              appearance="transparent"
              style={{
                color: theme.palette.purpleDark,
              }}
            />
            <Badge
              style={{
                position: "absolute",
                top: "-4px",
                right: "-8px",
                pointerEvents: "none",
                // Badge centers its content with inline-flex - "block" would
                // knock the number off-center.
                display: props.notifications?.length ? "inline-flex" : "none",
              }}
            >
              {props.notifications?.length}
            </Badge>
          </NotificationsButtonWrap>
          <SettingsMenu
            languageOptions={props.languageOptions || []}
            onSelect={props.onChangeLanguage}
            selectedLanguage={props.defaultLanguage || ""}
          />

          {headerConfig.account && props.user ? (
            <UserSettingsMenu
              userData={props.user}
              logout={props.logout as () => void}
            />
          ) : null}
        </ActionIconsWrap>
      </ContentWrap>
    </Container>
  );
};

export default GrecoHeader;

const Container = styled.div`
  background: ${({ theme }) => theme.palette.white};
  display: flex;
  width: 100%;

  line-height: ${sizes.appHeader}px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
  box-sizing: border-box;
`;

const ContentWrap = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: ${sizes.appHeader}px;
  padding: 0 10px;
  @media only screen and ${devices.md} {
  }
`;

const ActionIconsWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;

  .fui-Avatar__icon {
    background: ${({ theme }) => theme.palette.purpleDark};
    color: ${({ theme }) => theme.palette.white};
    font-weight: bold;
  }
  @media only screen and ${devices.md} {
    gap: 20px;
  }
`;

// Anchors the notifications badge to the bell button itself. Keeping the badge
// outside the Button also stops the Button from clipping it.
const NotificationsButtonWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const NavigationButton = styled(Button)`
  height: ${sizes.appHeader}px;
  width: 50px;
  max-width: 50px;
  max-height: ${sizes.appHeader}px;
  background: transparent;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.palette.purpleDark};
  .fui-Button__icon {
    width: 25px;
    height: 25px;
  }
  &:hover {
    color: ${({ theme }) => theme.palette.purpleDark};
    transition-property: transform;
    transition-duration: 0.2s;
    transform: scale(1.1);
  }
`;
